import { AxiosRequestConfig, AxiosResponse } from 'axios'
import { get, post } from 'common/HTTPClient'
import { DefaultHTTPResponse } from 'common/HTTPClient/DefaultHTTPResponse'
import { to } from 'common/helpers/awaitTo'
import { InstallationDetail, InstallationErrorPayload } from 'common/types/kraken-core/Installation'
import { PaginatedResponse } from 'common/types/kraken-core/Pagination'
import { SearchIntegrationsPageRequest } from './types'
import {
  IntegrationModule,
  IntegrationModuleList,
} from '../../../common/types/kraken-core/IntegrationModule'

function parseInstallError(error: unknown): InstallationErrorPayload {
  if (!error || typeof error !== 'object') {
    return { message: 'Installation failed' }
  }

  const axiosError = error as {
    response?: {
      data?: InstallationErrorPayload
    }
    message?: string
  }
  const data = axiosError.response?.data

  if (data && typeof data === 'object' && typeof data.message === 'string') {
    return {
      message: data.message,
      diagnostics: data.diagnostics,
      raw_output: data.raw_output,
    }
  }

  return { message: axiosError.message || 'Installation failed' }
}

export async function searchIntegrationModule(
  data?: SearchIntegrationsPageRequest,
  config?: AxiosRequestConfig
): Promise<DefaultHTTPResponse<PaginatedResponse<IntegrationModuleList>>> {
  const params: SearchIntegrationsPageRequest = {
    ...data,
    page: data?.page || 1,
    page_size: data?.page_size || 10,
  }
  const filterByName = data?.filters?.find(f => f.property === 'name').value
  if (filterByName) params.name = filterByName
  const [error, response] = await to(get('/integration_modules', { ...config, params }))
  return { error, response }
}

export async function getIntegrationModule(
  id: string,
  config?: AxiosRequestConfig
): Promise<DefaultHTTPResponse<IntegrationModule>> {
  const [error, response] = await to(get(`/integration_modules/${id}`, { ...config }))
  return { error, response }
}

export async function installIntegrationModuleViaSelfService(
  id: string,
  variables: Record<string, unknown>,
  config?: AxiosRequestConfig
): Promise<{ error?: InstallationErrorPayload; response?: AxiosResponse<InstallationDetail> }> {
  const [error, response] = await to(
    post(`/integration_modules/${id}/install-via-self-service`, variables, { ...config })
  )

  if (error) {
    return { error: parseInstallError(error), response }
  }

  return { error, response }
}
